/**
 * Templates API functions - TypeScript version
 * Authentication headers are handled automatically by the axios instance
 */

import { AxiosInstance } from "axios"
import {
  TemplateParseResponse,
  TemplateCatalogue,
  TemplateRef,
  TemplateInputValue,
  StagedBlock,
  TrayMergeResult,
} from "types/template.types"
import { CheckQuestionnaireResult } from "types/questionnaire.types"

/**
 * The project owner as the `hint_owner` query parameter (the Conductor reads
 * the project's questionnaire with it, as `questionnaire-get` does).
 *
 * @param owner - The owner of the project
 */
function ownerQuery(owner: string): string {
  return `hint_owner=${encodeURIComponent(owner)}`
}

/**
 * Lists every template the unit can use, public and private, with its
 * category, description, inputs and raw merge text.
 *
 * @param axiosInstance - Authenticated axios instance
 * @returns The catalogue (`{ templates }`)
 * @throws Will throw an error if the request fails
 */
export async function getTemplateCatalogue(
  axiosInstance: AxiosInstance
): Promise<TemplateCatalogue> {
  const response = await axiosInstance.get<TemplateCatalogue>(
    `/template-catalogue`
  )
  return response.data
}

/**
 * Parses one template against the project's questionnaire (so the engine
 * knows the names and exceptions already in use).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param req - The project id and owner, and the template reference
 * @returns The `templateParse` answer (`{ result: { information, sections } }`)
 * @throws Will throw an error if the request fails
 */
export async function parseTemplate(
  axiosInstance: AxiosInstance,
  req: TemplateRef & { id: string; owner: string }
): Promise<TemplateParseResponse["templateParse"]> {
  const response = await axiosInstance.post<TemplateParseResponse>(
    `/template-parse?${ownerQuery(req.owner)}`,
    {
      id: req.id,
      file: req.file,
      access: req.access,
      template: req.template,
    }
  )
  return response.data.templateParse
}

/**
 * Merges one template with its filled-in inputs.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param req - The project id and owner, the template reference, the inputs (string
 * values) and the ticked exceptions
 * @returns The merged questionnaire text
 * @throws Will throw an error if the request fails
 */
export async function mergeTemplate(
  axiosInstance: AxiosInstance,
  req: TemplateRef & {
    id: string
    owner: string
    inputs: TemplateInputValue[]
    exceptions: string[]
  }
): Promise<string> {
  const response = await axiosInstance.post<string>(
    `/template-merge?${ownerQuery(req.owner)}`,
    {
      id: req.id,
      file: req.file,
      access: req.access,
      template: req.template,
      inputs: req.inputs,
      exceptions: req.exceptions,
    },
    { responseType: "text" }
  )
  return response.data
}

/**
 * Merges every block of the staging tray in one go. The server checks the
 * NAME inputs against the questionnaire and against each other; a clash
 * answers 422, which surfaces as an axios error whose `response.data.blocks`
 * is a `TrayNameClash` (0-based block indexes).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param req - The project id and owner, and the staged blocks, in tray order
 * @returns The joined text and each block's line range in it
 * @throws Will throw an error if the request fails or a name clashes
 */
export async function mergeTemplateTray(
  axiosInstance: AxiosInstance,
  req: { id: string; owner: string; blocks: StagedBlock[] }
): Promise<TrayMergeResult> {
  const response = await axiosInstance.post<TrayMergeResult>(
    `/template-merge-tray?${ownerQuery(req.owner)}`,
    {
      id: req.id,
      blocks: req.blocks.map((block) => ({
        file: block.ref.file,
        access: block.ref.access,
        template: block.ref.template,
        inputs: block.inputs,
        exceptions: block.exceptions,
      })),
    }
  )
  return response.data
}

/**
 * Checks a questionnaire text without saving it. Any of `errors`,
 * `warnings` and `unfinished` may be absent from the notifications.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param text - The plain questionnaire text
 * @returns The engine check (`{ parse, notifications }`)
 * @throws Will throw an error if the request fails
 */
export async function validateQuestionnaireText(
  axiosInstance: AxiosInstance,
  text: string
): Promise<CheckQuestionnaireResult> {
  const response = await axiosInstance.post<{
    result: CheckQuestionnaireResult
  }>(`/validate-questionnaire`, text, {
    headers: { "Content-Type": "text/plain" },
  })
  return response.data.result
}
